import React, { useState, useEffect, useRef } from "react";
import { BsTelephone, BsEnvelopePaper, BsChatText } from "react-icons/bs";
import { Button, Input, Textarea } from "@material-tailwind/react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import bangaloreImg from "../../../assets/bangalore.jpeg";
import ariyalurImg from "../../../assets/ariyalur.jpeg";
import coimbatoreImg from "../../../assets/coimbatore.jpeg";
import tuticorinImg from "../../../assets/tuticorin.jpeg";
import tel from "../../../assets/tel.jpeg";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CONTACT_US_API } from "../../Api";
import axios from "axios";

export default function ContactUsPage() {
    // Animation controls for the outlet cards
    const controls = useAnimation();

    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
    useEffect(() => {
        if (inView) {
            controls.start("visible");
        } else {
            controls.start("hidden");
        }
    }, [inView, controls]);

    // Outlet data for "Our Service Outlets" section
    const outletData = [
        { title: "Ariyalur", img: ariyalurImg },
        { title: "Bangalore", img: bangaloreImg },
        { title: "Tuticorin", img: tuticorinImg },
        { title: "Coimbatore", img: coimbatoreImg },
    ];

    // Form state
    const [organizationName, setOrganizationName] = useState("");
    const [fullName, setFullName] = useState("");
    const [contactNumber, setContactNumber] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [description, setDescription] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if any field is empty
        if (!organizationName || !fullName || !contactNumber || !emailAddress || !description) {
            toast.error("Please fill all the fields before submitting!", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        // Prepare form data
        const formData = new FormData();
        formData.append("orgName", organizationName);
        formData.append("personName", fullName);
        formData.append("orgEmail", emailAddress);
        formData.append("orgContactNumber", contactNumber);
        formData.append("description", description);

        try {
            const response = await axios.post(CONTACT_US_API, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.status >= 200 && response.status < 300) {
                // Success Message
                toast.success("Thanks for reaching out to us! Our team will contact you shortly.", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });

                // Reset the form after successful submission
                setOrganizationName("");
                setFullName("");
                setContactNumber("");
                setEmailAddress("");
                setDescription("");
            } else {
                toast.error("Something went wrong! Please try again later.", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        } catch (error) {
            console.error("Error in sending mail:", error.response?.data || error.message);

            toast.error("Failed to send your request. Please check your details and try again.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    };

    const leftColumnFAQs = [
        {
            question: "How long does it take to respond?",
            answer:
                "Our support team typically responds within 24 hours on weekdays. During peak seasons or holidays, it may take slightly longer, but we always strive to address all inquiries as quickly as possible. For urgent matters, please call our support line directly.",
        },
        {
            question: "Can I change my order once placed?",
            answer:
                "Yes! If you need to modify your order, please contact us as soon as possible. We’ll do our best to accommodate changes before the order is processed and shipped. If the order has already been dispatched, we can guide you on next steps for returns or exchanges.",
        },
        {
            question: "Do you offer international shipping?",
            answer:
                "Absolutely. We ship to most countries worldwide using reliable carriers. International shipping times vary by destination, and you may be responsible for customs duties or import taxes upon arrival. Please consult our shipping policy for more details or reach out to us with any questions.",
        },
    ];

    const rightColumnFAQs = [
        {
            question: "How can I track my order?",
            answer:
                "Once your order ships, you will receive a tracking number via email. You can use this tracking number on our carrier’s website to view real-time updates. If you have any trouble locating your tracking information, contact our support team for assistance.",
        },
        {
            question: "What is your return policy?",
            answer:
                "We accept returns within 30 days of delivery, provided the item is in its original condition and packaging. To initiate a return, please contact our support team with your order details. Once approved, we’ll provide instructions on how to ship the product back. Refunds are processed within 5–7 business days after we receive the returned item.",
        },
    ];


    const [openLeft, setOpenLeft] = useState(new Set([0]));
    // Right column: no questions open initially
    const [openRight, setOpenRight] = useState(new Set());

    // Toggle function for left column
    const toggleLeft = (index) => {
        setOpenLeft((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index); // close if open
            } else {
                newSet.add(index); // open if closed
            }
            return newSet;
        });
    };

    // Toggle function for right column
    const toggleRight = (index) => {
        setOpenRight((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const promoRef = useRef(null);

    const handleScrollToPromo = () => {
        if (promoRef.current) {
            promoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
            <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-10 ">
                {/* Left Side - Text */}
                <div className="lg:w-[1000px] text-start mt-40 lg:mt-60">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold">We'd Love to Hear from You. Let's collaborate our journy together!</h1>
                    {/* <h4 className="text-4xl md:text-5xl font-bold mt-4">Let's collaborate our journy together.</h4> */}

                    <p
                        className="text-gray-600 mt-4 text-base sm:text-lg md:text-xl max-w-3xl"
                        style={{ fontFamily: "'Poppins', sans-serif", fontWeight: "400" }}
                    >
                        Thanks for stopping by! Whether you have a question, comment, or just want to say hi, don't be a stranger. We'r here to help, and you would love to connecting with our community.
                    </p>
                    <Button
                        color="green"
                        variant="gradient"
                        size="sm"
                        className="mt-10 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 "
                        onClick={handleScrollToPromo}
                    >
                        <span className="py-2 text-base">
                            schedule a Call
                        </span>
                    </Button>
                </div>

                {/* Right Side - Image */}
                <div className="hidden md:flex justify-end mt-20 lg:mt-20">
                    <img
                        src={tel}
                        alt="Teamwork illustration"
                        className="bg-cover bg-no-repeat bg-center h-[600px]"
                    />
                </div>

            </div>


            {/* Title */}
            <div ref={promoRef} className="my-12">             </div>
            <h1 className="text-3xl md:text-5xl font-bold text-green-800 mb-4">
                Schedule a call.
            </h1>

            <h2 className="text-2xl md:text-4xl font-bold mb-6">
                <span className="text-gray-600 font-semibold text-md">
                    Discuss your needs and discover our tailor-made solutions.
                </span>
            </h2>


            {/* 2-Column Layout: Form & Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-5">
                {/* Left Column: Contact Cards with Equal Height */}
                <div className="flex flex-col gap-4 flex-grow">
                    {/* Contact Sales */}
                    <div className="flex flex-col justify-between p-6 border-2 border-gray-300 rounded-2xl flex-grow">
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-gray-800">Contact Sales</h3>
                            <p className="text-sm text-gray-600">
                                Have questions about our products? Our sales team is available from 9:30 AM to 6:30 PM.
                            </p>
                            <p className="text-sm text-gray-500">
                                <a href="tel:+919790935128" className="underline">+91 9790935128</a>,{" "}
                                <a href="tel:04435661951" className="underline">044 - 35661951</a>,{" "}<a href="mailto:admin@sunteknolozy.com" className="underline">admin@sunteknolozy.com</a>
                            </p>
                        </div>
                        {/* Icons */}
                        <div className="flex items-center gap-4 mt-4">
                            <a href="tel:+919790935128">
                                <BsTelephone className="text-[25px] text-green-500" />
                            </a>
                            <a href="tel:044 - 35661951">
                                <BsChatText className="text-[25px] text-green-500" />
                            </a>
                            <a href="mailto:admin@sunteknolozy.com">
                                <BsEnvelopePaper className="text-[25px] text-green-500" />
                            </a>
                        </div>
                    </div>

                    {/* Contact Service */}
                    <div className="flex flex-col justify-between p-6 border-2 border-gray-300 rounded-2xl flex-grow">
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-gray-800">Contact Service</h3>
                            <p className="text-sm text-gray-600">
                                Need support or service? Our team is here to help.
                            </p>
                            <p className="text-sm text-gray-500">
                                <a href="tel:+917598725128" className="underline">+91 7598725128</a>,{" "}
                                <a href="tel:04435661951" className="underline">044 - 35661951</a>,{" "}<a href="mailto:service@sunteknolozy.com" className="underline">service@sunteknolozy.com</a>
                            </p>
                        </div>
                        {/* Icons */}
                        <div className="flex items-center gap-4 mt-4">
                            <a href="tel:044 - 35661951">
                                <BsTelephone className="text-[25px] text-green-500" />
                            </a>
                            <a href="tel:+917598725128">
                                <BsChatText className="text-[25px] text-green-500" />
                            </a>
                            <a href="mailto:service@sunteknolozy.com">
                                <BsEnvelopePaper className="text-[25px] text-green-500" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Right Column: Contact Form */}
                <div className="">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {/* Organization Name */}
                        <div>
                            <Input
                                type="text"
                                label="Organization Name"
                                value={organizationName}
                                onChange={(e) => setOrganizationName(e.target.value)}
                                className="w-full"
                            />
                        </div>

                        {/* Full Name & Contact Number */}
                        <div className="flex flex-col md:flex-row gap-4">
                            <Input
                                type="text"
                                label="Full Name"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="flex-1"
                            />
                            <Input
                                type="text"
                                label="Contact Number"
                                value={contactNumber}
                                onChange={(e) => setContactNumber(e.target.value)}
                                className="flex-1"
                            />
                        </div>

                        {/* Email Address */}
                        <div>
                            <Input
                                type="email"
                                label="Email Address"
                                value={emailAddress}
                                onChange={(e) => setEmailAddress(e.target.value)}
                                className="w-full"
                            />
                        </div>

                        {/* Description / Use Case */}
                        <div>
                            <Textarea
                                label="Describe your requirements"
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full"
                            />
                        </div>

                        {/* Submit Button */}
                        <div className="text-right">
                            <Button type="submit" className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-md w-full">
                                Submit
                            </Button>
                        </div>
                    </form>
                </div>
            </div>


            {/* Map Section */}
            <div className="space-y-4 py-12">
                <div className="w-full h-64 md:h-[450px] rounded-md overflow-hidden">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d124496.95631344663!2d79.94679620292966!3d12.80899853539166!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5267030c426185%3A0x98020b7b1e16afc2!2sSUN%20TEKNOLOZY!5e0!3m2!1sen!2sin!4v1742821212362!5m2!1sen!2sin"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="SUN TEKNOLOZY Location Map"
                    ></iframe>
                </div>
            </div>

            {/* Service Outlets Section */}
            <div ref={ref} className="space-y-6 py-12">
                <h2
                    className="text-2xl md:text-4xl font-bold mb-6"
                >
                    <span className="text-green-800">Our service outlets. </span>
                    <span className="text-gray-600 font-semibold text-md">Always within reach.</span>
                </h2>
                <div className="flex flex-col md:flex-row gap-6 justify-center items-center w-full py-5">
                    {outletData.map((item, index) => (
                        <motion.div
                            key={index}
                            className="w-full md:w-1/3 relative text-white group overflow-hidden rounded-2xl"
                            variants={{
                                hidden: { opacity: 0, y: 50 },
                                visible: {
                                    opacity: 1,
                                    y: 0,
                                    transition: { duration: 0.8, delay: 0.2 + index * 0.2 },
                                },
                            }}
                            whileHover="hover"
                            initial="hidden"
                            animate={controls}
                        >
                            <img
                                src={item.img}
                                alt={item.title}
                                className="w-full rounded-2xl h-[500px] object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/40 rounded-2xl"></div>
                            <motion.div
                                variants={{ initial: { y: 0 }, hover: { y: -30 } }}
                                transition={{ duration: 0, ease: "easeOut" }}
                                className="absolute bottom-6 left-6 z-10 transition-all duration-300"
                            >
                                <h3 className="text-xl font-bold pb-4">{item.title}</h3>
                            </motion.div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* FAQ Section */}
            <div className="space-y-6 pt-12">
                {/* Heading & Subheading */}
                <div className="text-left space-y-2 mb-6">

                    <h2
                        className="text-2xl md:text-4xl font-bold mb-6"
                    >
                        <span className="text-green-800">Got any questions? </span>
                        <span className="text-gray-600 font-semibold text-md">We've got answers.</span>
                    </h2>
                </div>

                {/* FAQ Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-5">

                    {/* Left Column */}
                    <div className="space-y-4">
                        {leftColumnFAQs.map((faq, index) => {
                            const isOpen = openLeft.has(index);
                            return (
                                <div
                                    key={index}
                                    className="p-4 border-2 border-gray-300 rounded-2xl"
                                >
                                    {/* Question & Icon Row */}
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-lg text-black">
                                            {faq.question}
                                        </h3>
                                        {/* Icon toggles between + and - */}
                                        <span
                                            className="text-xl text-gray-500 font-bold cursor-pointer select-none"
                                            onClick={() => toggleLeft(index)}
                                        >
                                            {isOpen ? "−" : "+"}
                                        </span>
                                    </div>
                                    {/* Answer: only show if open */}
                                    {isOpen && (
                                        <p className="text-gray-700 mt-2">{faq.answer}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        {rightColumnFAQs.map((faq, index) => {
                            const isOpen = openRight.has(index);
                            return (
                                <div
                                    key={index}
                                    className="p-4 border-2 border-gray-300 rounded-2xl"
                                >
                                    {/* Question & Icon Row */}
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-lg text-black">
                                            {faq.question}
                                        </h3>
                                        {/* Icon toggles between + and - */}
                                        <span
                                            className="text-xl text-gray-500 font-bold cursor-pointer select-none"
                                            onClick={() => toggleRight(index)}
                                        >
                                            {isOpen ? "−" : "+"}
                                        </span>
                                    </div>
                                    {/* Answer: only show if open */}
                                    {isOpen && (
                                        <p className="text-gray-700 mt-2">{faq.answer}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
};

